import React, { useState } from "react";
import { Bar, Line, Pie } from "react-chartjs-2";
import Modal from "./Modal"; // Assume this is a modal component you've created
import { Chart as ChartJS } from "chart.js/auto";
import Energy from "../../assets/Energy.png";
import roofMaterial from "../../assets/roofMaterial.png";
import rooftypeVsPanelCount from "../../assets/rooftypeVsPanelCount.png";
import Graph from "../../assets/graph.png";

const Dashboard = ({ data }) => {
  const [selectedGraph, setSelectedGraph] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        type: "linear",
        beginAtZero: true,
      },
    },
  };

  // Assume BarChartData, BarChartDataRoofMaterial, LineChartDataRoofType, and pieChartData are defined here
  // Calculate min, max, and average
  const minEnergy = Math.min(...data.map((d) => d.yearly_energy_dc_kwh));
  const maxEnergy = Math.max(...data.map((d) => d.yearly_energy_dc_kwh));
  const avgEnergy =
    data.reduce((acc, curr) => acc + curr.yearly_energy_dc_kwh, 0) /
    data.length;

  // Update BarChartData
  const BarChartData = {
    labels: ["Min Energy", "Max Energy", "Average Energy"],
    datasets: [
      {
        label: "Yearly Energy (DC kWh)",
        data: [minEnergy, maxEnergy, avgEnergy],
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
        ],
        borderColor: [
          "rgba(255,99,132,1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  // BArchart for roof material
  const roofMaterialsData = [
    ...new Set(
      data
        .filter((d) => d.roof_material !== "no_info")
        .map((d) => d.roof_material)
    ),
  ];

  const BarChartDataRoofMaterial = {
    labels: roofMaterialsData,
    datasets: [
      {
        label: "Roof Material",
        data: roofMaterialsData.map(
          (material) => data.filter((d) => d.roof_material === material).length
        ),
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  // Pie Chart - Roof Material Distribution
  const roofMaterials = [
    ...new Set(
      data
        .filter((d) => d.roof_material !== "no_info")
        .map((d) => d.roof_material)
    ),
  ];
  const pieChartData = {
    labels: roofMaterials,
    datasets: [
      {
        label: "Roof Material Distribution",
        data: roofMaterials.map(
          (material) => data.filter((d) => d.roof_material === material).length
        ),
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  // How to plot graph of roof type and average panel count
  const roofType = [
    ...new Set(
      data
        .filter((d) => d.roof_material !== "no_info")
        .map((d) => d.roof_material)
    ),
  ];

  const avgPanelCount = roofType.map((type) => {
    const filteredData = data.filter((d) => d.roof_material === type);
    return (
      filteredData.reduce((acc, curr) => acc + curr.panels_count, 0) /
      filteredData.length
    );
  });

  const LineChartDataRoofType = {
    labels: roofType,
    datasets: [
      {
        label: "Average Panel Count",
        data: avgPanelCount,
        fill: false,
        borderColor: "rgba(75, 192, 192, 1)",
        tension: 0.1,
      },
    ],
  };

  const openModal = (graphName) => {
    setSelectedGraph(graphName);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const renderGraph = (graphName) => {
    switch (graphName) {
      case "BarChartData":
        return (
          <div
            className="w-full h-full p-4"
            style={{ width: "1100px", height: "600px", overflow: "auto" }}
          >
            <Bar data={BarChartData} options={chartOptions} />{" "}
          </div>
        );
      case "BarChartDataRoofMaterial":
        return (
          <div
            className="w-full h-full p-4"
            style={{ width: "1100px", height: "600px", overflow: "auto" }}
          >
            <Bar data={BarChartDataRoofMaterial} options={chartOptions} />{" "}
          </div>
        );
      case "LineChartDataRoofType":
        return (
          <div
            className="w-full h-full p-4"
            style={{ width: "1100px", height: "600px", overflow: "auto" }}
          >
            <Line data={LineChartDataRoofType} options={chartOptions} />{" "}
          </div>
        );
      case "pieChartData":
        return (
          <div
            className="w-full h-full p-4"
            style={{ width: "1100px", height: "600px", overflow: "auto" }}
          >
            <Pie data={pieChartData} options={chartOptions} />{" "}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="w-full h-full p-4"
      style={{
        maxHeight: "80vh",
        overflow: "auto",
        width: "100%",
        height: "100%",
      }}
    >
      <div
        className="grid-tiles"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "10px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div>
          <div
            className="graph-tile"
            style={{
              aspectRatio: "1 / 1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "1px solid #ccc", // Optional, for visual structure
              padding: "10px",
              boxSizing: "border-box",
              borderRadius: "10px", // Round edges
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)", // Shadow
            }}
            onClick={() => openModal("BarChartData")}
          >
            <img src={Energy} alt="Graph" className="w-60 h-30" />
            {/* <p>Yearly Energy (DC kWh)</p> */}
          </div>
          <div className="text-center">
            <h3>Energy matrix</h3>
          </div>
        </div>
        <div>
          <div
            className="graph-tile"
            style={{
              aspectRatio: "1 / 1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "1px solid #ccc",
              padding: "10px",
              boxSizing: "border-box",
              borderRadius: "10px", // Round edges
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)", // Shadow
            }}
            onClick={() => openModal("BarChartDataRoofMaterial")}
          >
            <img src={roofMaterial} alt="Graph" className="w-60 h-30" />
          </div>
          <div className="text-center">
            <h3>Roof Material</h3>
          </div>
        </div>
        <div>
          <div
            className="graph-tile"
            style={{
              aspectRatio: "1 / 1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "1px solid #ccc",
              padding: "10px",
              boxSizing: "border-box",
              borderRadius: "10px", // Round edges
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)", // Shadow
            }}
            onClick={() => openModal("LineChartDataRoofType")}
          >
            <img src={rooftypeVsPanelCount} alt="Graph" className="w-60 h-30" />
          </div>
          <div className="text-center">
            <h3>Roof type vs Panel count</h3>
          </div>
        </div>
        <div>
          <div
            className="graph-tile"
            style={{
              aspectRatio: "1 / 1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "1px solid #ccc",
              padding: "10px",
              boxSizing: "border-box",
              borderRadius: "10px", // Round edges
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)", // Shadow
            }}
            onClick={() => openModal("pieChartData")}
          >
            <img src={Graph} alt="Graph" className="w-60 h-30" />
          </div>
          <div className="text-center">
            <h3>Roof type distribution</h3>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={closeModal}>
          {renderGraph(selectedGraph)}
        </Modal>
      )}
    </div>
  );
};

export default Dashboard;
